<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Land Search</span>
            </nav>
            <h1 class="page-title">Land Search</h1>
            <p class="page-subtitle">Search property records, view land rates, and make payments</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </div>
                  <h3>Property Search</h3>
                </div>
                <p class="info-description">
                  Search for property records using plot number, owner name, or ID number. View land rates, outstanding balances, and make payments online.
                </p>

                <div class="search-options">
                  <h4>You Can Search By</h4>
                  <ul>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      LR Number (Land Reference)
                    </li>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Owner Name
                    </li>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      ID/Passport Number
                    </li>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Phone Number
                    </li>
                  </ul>
                </div>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Property Lookup</strong>
                      <p>Find property details instantly</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>View Statements</strong>
                      <p>Check rates and balances</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Pay Online</strong>
                      <p>Pay land rates via M-Pesa</p>
                    </div>
                  </div>
                </div>

                <div class="help-box">
                  <h4>Need Help?</h4>
                  <p>Contact the Lands Department for property inquiries.</p>
                  <a href="/contact" class="help-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Contact Support
                  </a>
                </div>
              </div>
            </div>

            <!-- Right Side - Search & Results -->
            <div class="form-panel">
              <!-- Search Form -->
              <div class="form-card">
                <div class="form-card-header">
                  <h2>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    Search Property
                  </h2>
                  <p>Enter property details to search records</p>
                </div>

                <div class="form-card-body">
                  <!-- Search Input -->
                  <div class="search-form">
                    <div class="form-group">
                      <label class="form-label">Search Property <span class="required">*</span></label>
                      <div class="input-with-btn">
                        <input
                            type="text"
                            v-model="searchQuery"
                            class="form-input"
                            placeholder="Enter LR Number, Owner Name, ID Number, or Phone"
                            @keyup.enter="searchProperty"
                        />
                        <button type="button" class="search-btn" @click="searchProperty" :disabled="!searchQuery || searching">
                          <span v-if="searching" class="spinner small white"></span>
                          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                        </button>
                      </div>
                      <span class="form-hint">Search by LR number, owner name, ID number, or phone number</span>
                    </div>
                  </div>

                  <div v-if="error" class="error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    {{ error }}
                  </div>

                  <!-- Search Results -->
                  <div v-if="properties.length > 0" class="search-results">
                    <div class="results-header">
                      <h3>Search Results</h3>
                      <span class="results-count">{{ properties.length }} {{ properties.length === 1 ? 'property' : 'properties' }} found</span>
                    </div>

                    <div class="properties-list">
                      <div
                          v-for="property in properties"
                          :key="property.id"
                          class="property-card"
                          :class="{ selected: selectedProperty?.id === property.id }"
                          @click="selectProperty(property)"
                      >
                        <div class="property-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                          </svg>
                        </div>
                        <div class="property-info">
                          <strong class="plot-number">{{ property.LRNumber }}</strong>
                          <span class="owner-name">{{ property.ownerNames }}</span>
                          <span class="location">{{ property.wardName }}, {{ property.subCountyName }}</span>
                        </div>
                        <div class="property-balance" :class="{ 'has-balance': parseFloat(property.currentBalance || 0) > 0 }">
                          <span class="balance-label">Balance</span>
                          <strong>KES {{ formatAmount(property.currentBalance || 0) }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-else-if="!searching && searched && properties.length === 0" class="empty-state">
                    <div class="empty-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </div>
                    <h3>No Properties Found</h3>
                    <p>Try searching with different criteria or check the spelling</p>
                  </div>

                  <!-- Initial State -->
                  <div v-else-if="!searching && !searched" class="empty-state">
                    <div class="empty-icon initial">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    </div>
                    <h3>Search for Property</h3>
                    <p>Enter an LR number, owner name, ID number, or phone to find property records</p>
                  </div>
                </div>
              </div>

              <!-- Property Details Card -->
              <div v-if="selectedProperty" class="form-card property-details-card">
                <div class="form-card-header green">
                  <div class="header-content">
                    <h2>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      Property Details
                    </h2>
                    <p>{{ selectedProperty.LRNumber }}</p>
                  </div>
                  <button type="button" class="close-btn" @click="selectedProperty = null">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                <div class="form-card-body">
                  <!-- Property Info Grid -->
                  <div class="details-section">
                    <h3 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      Owner Information
                    </h3>
                    <div class="details-grid">
                      <div class="detail-item">
                        <span class="detail-label">Owner Name</span>
                        <span class="detail-value">{{ selectedProperty.ownerNames || '-' }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.ownerID">
                        <span class="detail-label">ID Number</span>
                        <span class="detail-value">{{ selectedProperty.ownerID }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.ownerPhoneNo">
                        <span class="detail-label">Phone Number</span>
                        <span class="detail-value">{{ selectedProperty.ownerPhoneNo }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.ownerEmail">
                        <span class="detail-label">Email</span>
                        <span class="detail-value">{{ selectedProperty.ownerEmail }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.physicalAddress">
                        <span class="detail-label">Physical Address</span>
                        <span class="detail-value">{{ selectedProperty.physicalAddress }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.pinNumber">
                        <span class="detail-label">PIN Number</span>
                        <span class="detail-value">{{ selectedProperty.pinNumber }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="details-section">
                    <h3 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      Property Information
                    </h3>
                    <div class="details-grid">
                      <div class="detail-item">
                        <span class="detail-label">LR Number</span>
                        <span class="detail-value">{{ selectedProperty.LRNumber }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.UPN">
                        <span class="detail-label">UPN</span>
                        <span class="detail-value">{{ selectedProperty.UPN }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.percelSize">
                        <span class="detail-label">Parcel Size (Ha)</span>
                        <span class="detail-value">{{ selectedProperty.percelSize }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.useName">
                        <span class="detail-label">Land Use</span>
                        <span class="detail-value">{{ selectedProperty.useName }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Sub County</span>
                        <span class="detail-value">{{ selectedProperty.subCountyName }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Ward</span>
                        <span class="detail-value">{{ selectedProperty.wardName }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.percelAddress">
                        <span class="detail-label">Parcel Address</span>
                        <span class="detail-value">{{ selectedProperty.percelAddress }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.block">
                        <span class="detail-label">Block</span>
                        <span class="detail-value">{{ selectedProperty.block }}</span>
                      </div>
                      <div class="detail-item" v-if="selectedProperty.zone">
                        <span class="detail-label">Zone</span>
                        <span class="detail-value">{{ selectedProperty.zone }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Status</span>
                        <span class="detail-value">{{ selectedProperty.status }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Balance Card -->
                  <div class="balance-card" :class="{ 'has-balance': parseFloat(selectedProperty.currentBalance || 0) > 0 }">
                    <div class="balance-header">
                      <span class="balance-title">Outstanding Balance</span>
                      <span class="balance-status" v-if="parseFloat(selectedProperty.currentBalance || 0) === 0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        Cleared
                      </span>
                      <span class="balance-status pending" v-else>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        Outstanding
                      </span>
                    </div>
                    <div class="balance-amount">
                      <span class="currency">KES</span>
                      <span class="amount">{{ formatAmount(selectedProperty.currentBalance || 0) }}</span>
                    </div>
                    <div class="balance-details">
                      <div class="balance-item" v-if="selectedProperty.annualLandRates">
                        <span>Annual Land Rates</span>
                        <strong>KES {{ formatAmount(selectedProperty.annualLandRates) }}</strong>
                      </div>
                      <div class="balance-item" v-if="selectedProperty.landRatesArrears && parseFloat(selectedProperty.landRatesArrears) > 0">
                        <span>Arrears</span>
                        <strong>KES {{ formatAmount(selectedProperty.landRatesArrears) }}</strong>
                      </div>
                      <div class="balance-item" v-if="selectedProperty.accumPenalty && parseFloat(selectedProperty.accumPenalty) > 0">
                        <span>Penalty</span>
                        <strong>KES {{ formatAmount(selectedProperty.accumPenalty) }}</strong>
                      </div>
                      <div class="balance-item" v-if="selectedProperty.accumInterest && parseFloat(selectedProperty.accumInterest) > 0">
                        <span>Interest</span>
                        <strong>KES {{ formatAmount(selectedProperty.accumInterest) }}</strong>
                      </div>
                      <div class="balance-item" v-if="selectedProperty.annualGroundRent && parseFloat(selectedProperty.annualGroundRent) > 0">
                        <span>Ground Rent</span>
                        <strong>KES {{ formatAmount(selectedProperty.annualGroundRent) }}</strong>
                      </div>
                      <div class="balance-item" v-if="selectedProperty.payments && parseFloat(selectedProperty.payments) > 0">
                        <span>Payments Made</span>
                        <strong>KES {{ formatAmount(selectedProperty.payments) }}</strong>
                      </div>
                    </div>
                  </div>

                  <!-- Payment Form (only show if balance > 0) -->
                  <div v-if="parseFloat(selectedProperty.currentBalance || 0) > 0" class="payment-section">
                    <h3 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                      Make Payment
                    </h3>

                    <div class="stk-payment-form">
                      <div class="form-group">
                        <label class="form-label">Amount to Pay</label>
                        <div class="input-with-currency">
                          <span class="currency-prefix">KES</span>
                          <input
                              type="number"
                              v-model="amountToPay"
                              class="form-input with-prefix"
                              placeholder="Enter amount"
                              :max="selectedProperty.currentBalance"
                          />
                        </div>
                        <div class="quick-amounts">
                          <button
                              type="button"
                              class="quick-btn"
                              :class="{ active: amountToPay == selectedProperty.currentBalance }"
                              @click="amountToPay = selectedProperty.currentBalance"
                          >
                            Full Balance
                          </button>
                          <button
                              v-if="parseFloat(selectedProperty.currentBalance) >= 2000"
                              type="button"
                              class="quick-btn"
                              @click="amountToPay = Math.floor(selectedProperty.currentBalance / 2)"
                          >
                            Half Amount
                          </button>
                          <button
                              v-if="selectedProperty.annualLandRates && parseFloat(selectedProperty.annualLandRates) > 0"
                              type="button"
                              class="quick-btn"
                              @click="amountToPay = selectedProperty.annualLandRates"
                          >
                            Annual Rate
                          </button>
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">M-Pesa Phone Number</label>
                        <input
                            type="tel"
                            v-model="phoneNumber"
                            class="form-input"
                            placeholder="e.g., 0712345678"
                        />
                        <span class="form-hint">You'll receive an STK push to this number</span>
                      </div>

                      <button
                          type="button"
                          class="btn btn-success btn-block"
                          @click="initiatePayment"
                          :disabled="!amountToPay || !phoneNumber || paymentLoading"
                      >
                        <span v-if="paymentLoading" class="spinner white small"></span>
                        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        </svg>
                        {{ paymentLoading ? 'Processing...' : `Pay KES ${formatAmount(amountToPay || 0)}` }}
                      </button>
                    </div>

                    <div class="manual-pay-info">
                      <h4>Or Pay Manually via M-Pesa</h4>
                      <div class="pay-details">
                        <div class="pay-item">
                          <span>Paybill:</span>
                          <strong>{{ paybillNumber }}</strong>
                        </div>
                        <div class="pay-item">
                          <span>Account:</span>
                          <strong>{{ selectedProperty.LRNumber }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Cleared Balance Message -->
                  <div v-else class="cleared-message">
                    <div class="cleared-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <h3>No Outstanding Balance</h3>
                    <p>This property has no outstanding land rates balance.</p>
                  </div>

                  <!-- Action Buttons -->
                  <div class="action-buttons">
                    <button type="button" class="btn btn-secondary" @click="generateStatement">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                      </svg>
                      View Statement
                    </button>
                    <button v-if="selectedProperty.billNo" type="button" class="btn btn-outline" @click="printReceipt(selectedProperty)">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                      Print Receipt
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Payment Modal -->
    <transition name="modal">
      <div v-if="payConfirmationPop" class="modal-overlay">
        <div class="modal-container payment-modal">
          <div class="payment-modal-header" :class="paymentReceived ? 'success' : 'pending'">
            <div v-if="!paymentReceived" class="payment-loader">
              <div class="loader-ring"></div>
            </div>
            <div v-else class="payment-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <p class="payment-status-text">
              {{ paymentReceived ? 'Payment Successful!' : 'Pending Land Rates Payment' }}
            </p>
            <h2 class="payment-amount">KES {{ formatAmount(amountToPay) }}</h2>
          </div>

          <div class="payment-modal-body">
            <div v-if="!paymentReceived" class="payment-instructions">
              <h4>Payment Procedure</h4>
              <p class="instruction-subtitle">Follow the steps below to complete your payment</p>
              <ol>
                <li>
                  <span class="step-num">1</span>
                  Check for a <strong>payment pop up</strong> on your phone
                </li>
                <li>
                  <span class="step-num">2</span>
                  Enter your <strong>M-Pesa PIN</strong> and click OK
                </li>
                <li>
                  <span class="step-num">3</span>
                  Wait for <strong>M-Pesa confirmation SMS</strong>
                </li>
                <li>
                  <span class="step-num">4</span>
                  Wait up to <strong>45 seconds</strong> as we process your transaction
                </li>
              </ol>
              <div class="waiting-indicator">
                <div class="pulse-dot"></div>
                <span>Waiting for payment confirmation...</span>
              </div>
            </div>

            <div v-if="paymentReceived" class="payment-success">
              <h4>Thank You {{ transaction.names || 'Customer' }}!</h4>
              <p class="success-message">Payment was successfully received.</p>

              <div class="success-details">
                <div class="success-row">
                  <span>Transaction Code</span>
                  <strong>{{ transaction.transaction_code }}</strong>
                </div>
                <div class="success-row">
                  <span>LR Number</span>
                  <strong>{{ selectedProperty.LRNumber }}</strong>
                </div>
                <div class="success-row">
                  <span>Amount Paid</span>
                  <strong>KES {{ formatAmount(amountToPay) }}</strong>
                </div>
              </div>

              <div class="success-actions">
                <button @click="printReceiptAfterPayment" class="btn btn-primary">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                  Print Receipt
                </button>
                <button @click="closePaymentModal" class="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <FooterComponent />
  </div>
</template>

<script>
import {biller, paysol, landrate, execute, execute2, authUrl} from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: 'LandSearch',
  components: { HeaderComponent, FooterComponent },

  data() {
    return {
      searchQuery: '',
      searching: false,
      searched: false,
      error: '',
      properties: [],
      selectedProperty: null,
      county: null,
      bank: null,
      // Payment
      phoneNumber: '',
      amountToPay: null,
      paymentLoading: false,
      payConfirmationPop: false,
      paymentReceived: false,
      transaction: {
        id: null,
        transaction_code: null,
        names: null
      },
      receiptDetails: {
        receiptNo: null
      }
    };
  },

  computed: {
    paybillNumber() {
      return this.bank?.payBill || this.county?.paybillNumber || '123456';
    }
  },

  mounted() {
    this.getCounty();
    this.checkUrlParams();
  },

  methods: {
    formatAmount(amount) {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('en-KE', { minimumFractionDigits: 0 });
    },

    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.county = res.data.data.county;
          this.bank = res.data.data.bank;
        }
      } catch (e) { console.error(e); }
    },

    checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const lrNumber = urlParams.get('LRNumber') || urlParams.get('lrNumber') || urlParams.get('plotNumber') || urlParams.get('plot');
      if (lrNumber) {
        this.searchQuery = lrNumber;
        this.searchProperty();
      }
    },

    async searchProperty() {
      if (!this.searchQuery) return;
      this.searching = true;
      this.searched = true;
      this.error = '';
      this.properties = [];
      this.selectedProperty = null;

      try {
        const data = new FormData();
        data.append("function", "getLandRates");
        data.append("search", this.searchQuery.trim());
        data.append("page", "1");
        data.append("rows_per_page", "20");
        data.append("dateFrom", "");
        data.append("dateTo", "");
        data.append("subCountyName", "");
        data.append("wardName", "");

        const res = await execute(data, landrate);

        if (res.data.success) {
          this.properties = res.data.data.properties || [];
          if (this.properties.length === 1) {
            this.selectProperty(this.properties[0]);
          }
        } else {
          this.error = res.data.message || 'No properties found. Please check your search criteria.';
        }
      } catch (e) {
        this.error = 'Error searching for property. Please try again.';
        console.error(e);
      } finally {
        this.searching = false;
      }
    },

    selectProperty(property) {
      this.selectedProperty = property;
      this.amountToPay = property.currentBalance || 0;
      this.phoneNumber = property.ownerPhoneNo || '';
    },

    async initiatePayment() {
      if (!this.phoneNumber) {
        this.error = "Please enter your M-Pesa phone number";
        return;
      }

      if (!this.amountToPay || this.amountToPay <= 0) {
        this.error = "Please enter a valid amount";
        return;
      }

      this.error = '';
      this.paymentLoading = true;
      this.payConfirmationPop = true;

      const data = new FormData();
      data.append("function", "customerPayBillOnline");
      data.append("payBillNumber", this.paybillNumber);
      data.append("amount", this.amountToPay);
      data.append("accountReference", this.selectedProperty.LRNumber);
      data.append("transactionDesc", `Land Rates - ${this.selectedProperty.LRNumber}`);
      data.append("phoneNumber", this.phoneNumber);
      data.append("token", "im05WXYH2rwRruPjCICieOs8m4E8IoltnDEhyPUv6bnB9cU60gD48SnJPC6oh7EpsPaAUGC8wqIdtVVjGlWLxqFssshxMHxHjEQJ");

      try {
        const res = await execute2(data, paysol);
        this.paymentLoading = false;

        if (res.data.success) {
          const intervalId = setInterval(() => {
            this.checkPayment()
                .then((result) => {
                  if (result) {
                    clearInterval(intervalId);
                  }
                })
                .catch((error) => {
                  console.error(error);
                });
          }, 2000);
        } else {
          this.payConfirmationPop = false;
          this.error = res.data.message || 'Failed to initiate payment. Please try again.';
        }
      } catch (e) {
        this.paymentLoading = false;
        this.payConfirmationPop = false;
        this.error = e.message || 'Payment request failed. Please try again.';
      }
    },

    checkPayment() {
      const data = new FormData();
      data.append("function", "checkPayment");
      data.append("accountRef", this.selectedProperty.LRNumber);

      return new Promise((resolve, reject) => {
        execute(data, biller)
            .then((res) => {
              if (res.data.success) {
                this.paymentLoading = false;
                this.transaction = res.data.data.transaction;
                this.receiptDetails = res.data.data.receipt?.receiptDetails || {};
                this.paymentReceived = true;
                resolve(true);
              } else {
                resolve(false);
              }
            })
            .catch((error) => {
              reject(error);
            });
      });
    },

    closePaymentModal() {
      this.payConfirmationPop = false;
      this.paymentReceived = false;
      this.paymentLoading = false;
      // Refresh property details
      this.searchProperty();
    },

    printReceipt(item) {
      const receipt = item || this.selectedProperty;

      if (!receipt.transactionCode && !receipt.receiptNo) {
        this.error = "Receipt number not available";
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: receipt.transactionCode || receipt.receiptNo,
        billNo: receipt.billNo
      });

      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    printReceiptAfterPayment() {
      const receiptNo = this.transaction?.transaction_code || this.receiptDetails?.receiptNo;

      if (!receiptNo) {
        this.error = "Receipt number not available yet. Please try again.";
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: receiptNo,
        billNo: this.selectedProperty.billNo || this.selectedProperty.LRNumber
      });

      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    generateStatement() {
      if (!this.selectedProperty) return;

      localStorage['params'] = JSON.stringify({
        plotNumber: this.selectedProperty.LRNumber,
        propertyId: this.selectedProperty.id
      });

      const routeData = this.$router.resolve({ name: 'land-statement' });
      window.open(routeData.href, '_blank');
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.page-wrapper { font-family: 'Plus Jakarta Sans', sans-serif; background: #f5f7fa; min-height: 100vh; }
.main-content { padding-bottom: 80px; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

/* Hero */
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 50%, #0a3459 100%); padding: 120px 0 60px /* clear fixed header */; position: relative; overflow: hidden; }
.page-hero::before { content: ''; position: absolute; top: 0; right: 0; width: 500px; height: 500px; background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%); transform: translate(30%, -30%); }
.hero-content { text-align: center; position: relative; z-index: 1; }

.breadcrumb-nav { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; font-size: 14px; }

.page-title { font-size: 36px; font-weight: 800; color: white; margin: 0 0 12px; letter-spacing: -0.5px; }
.page-subtitle { font-size: 16px; color: rgba(255,255,255,0.8); margin: 0; }

/* Content */
.content-section { margin-top: -30px; position: relative; z-index: 10; }
.content-grid { display: grid; grid-template-columns: 320px 1fr; gap: 32px; align-items: start; }

/* Info Panel */
.info-panel { position: sticky; top: 24px; }
.info-card { background: white; border-radius: 16px; padding: 28px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
.info-card-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0; }
.info-icon { width: 56px; height: 56px; background: linear-gradient(135deg, #059669 0%, #047857 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: white; }
.info-card-header h3 { font-size: 20px; font-weight: 700; color: #1e293b; margin: 0; }
.info-description { font-size: 14px; color: #64748b; line-height: 1.6; margin-bottom: 24px; }

.search-options { background: #f0fdf4; border-radius: 12px; padding: 20px; margin-bottom: 24px; }
.search-options h4 { font-size: 14px; font-weight: 700; color: #166534; margin: 0 0 12px; }
.search-options ul { list-style: none; padding: 0; margin: 0; }
.search-options li { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #15803d; padding: 8px 0; }
.search-options li svg { color: #059669; flex-shrink: 0; }

.info-features { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
.feature-item { display: flex; gap: 14px; align-items: flex-start; }
.feature-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #ede9fe; color: #7c3aed; }
.feature-item strong { display: block; font-size: 14px; font-weight: 600; color: #1e293b; }
.feature-item p { font-size: 12px; color: #64748b; margin: 2px 0 0; }

.help-box { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-radius: 12px; padding: 20px; }
.help-box h4 { font-size: 14px; font-weight: 700; color: #1e3a5f; margin: 0 0 8px; }
.help-box p { font-size: 13px; color: #475569; margin: 0 0 16px; }
.help-link { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #0f4c81; text-decoration: none; }
.help-link:hover { text-decoration: underline; }

/* Form Card */
.form-panel { display: flex; flex-direction: column; gap: 24px; }
.form-card { background: white; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); overflow: hidden; }
.form-card-header { background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 24px 32px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.form-card-header h2 { display: flex; align-items: center; gap: 12px; font-size: 20px; font-weight: 800; color: #1e293b; margin: 0; }
.form-card-header p { font-size: 14px; color: #64748b; margin: 4px 0 0; }
.form-card-header.green { background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); border-bottom-color: #6ee7b7; }
.form-card-header.green h2 { color: #166534; }
.form-card-header.green p { color: #15803d; }
.header-content { flex: 1; }
.close-btn { width: 36px; height: 36px; background: rgba(255,255,255,0.8); border: none; border-radius: 8px; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.close-btn:hover { background: white; color: #dc2626; }
.form-card-body { padding: 32px; }

/* Search Form */
.search-form { margin-bottom: 24px; }
.form-group { margin-bottom: 20px; }
.form-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px; }
.required { color: #ef4444; }
.form-hint { display: block; font-size: 12px; color: #64748b; margin-top: 6px; }

.input-with-btn { display: flex; gap: 12px; }
.form-input { flex: 1; width: 100%; padding: 14px 16px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 15px; font-family: inherit; transition: all 0.2s; background: white; }
.form-input:focus { outline: none; border-color: #0f4c81; box-shadow: 0 0 0 3px rgba(15, 76, 129, 0.1); }
.form-input::placeholder { color: #94a3b8; }
.search-btn { width: 52px; height: 52px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); border: none; border-radius: 10px; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0; }
.search-btn:hover:not(:disabled) { transform: scale(1.05); }
.search-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Error */
.error-message { display: flex; align-items: center; gap: 12px; padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; color: #dc2626; font-size: 14px; margin-bottom: 20px; }

/* Search Results */
.search-results { }
.results-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.results-header h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; }
.results-count { font-size: 13px; color: #64748b; background: #f1f5f9; padding: 4px 12px; border-radius: 20px; }

.properties-list { display: flex; flex-direction: column; gap: 12px; max-height: 400px; overflow-y: auto; }
.property-card { display: flex; align-items: center; gap: 16px; padding: 18px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 14px; cursor: pointer; transition: all 0.2s; }
.property-card:hover { border-color: #0f4c81; background: #f0f9ff; }
.property-card.selected { border-color: #059669; background: #f0fdf4; }
.property-icon { width: 48px; height: 48px; background: linear-gradient(135deg, #059669 0%, #047857 100%); border-radius: 12px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
.property-info { flex: 1; }
.plot-number { display: block; font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
.owner-name { display: block; font-size: 13px; color: #475569; margin-bottom: 2px; }
.location { display: block; font-size: 12px; color: #94a3b8; }
.property-balance { text-align: right; }
.property-balance .balance-label { display: block; font-size: 11px; color: #64748b; text-transform: uppercase; }
.property-balance strong { font-size: 15px; color: #059669; }
.property-balance.has-balance strong { color: #dc2626; }

/* Empty State */
.empty-state { text-align: center; padding: 60px 20px; }
.empty-icon { width: 100px; height: 100px; background: #f1f5f9; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; color: #94a3b8; }
.empty-icon.initial { background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); color: #059669; }
.empty-state h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
.empty-state p { font-size: 14px; color: #64748b; margin: 0; }

/* Details Section */
.details-section { margin-bottom: 24px; }
.section-title { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 16px; padding-bottom: 12px; border-bottom: 2px solid #e2e8f0; }
.details-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.detail-item { background: #f8fafc; padding: 14px 18px; border-radius: 10px; }
.detail-label { display: block; font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
.detail-value { font-size: 14px; font-weight: 600; color: #1e293b; }

/* Balance Card */
.balance-card { background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); border: 2px solid #6ee7b7; border-radius: 16px; padding: 24px; margin-bottom: 24px; }
.balance-card.has-balance { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-color: #fbbf24; }
.balance-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.balance-title { font-size: 14px; font-weight: 600; color: #047857; }
.balance-card.has-balance .balance-title { color: #92400e; }
.balance-status { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #059669; }
.balance-status.pending { color: #d97706; }
.balance-amount { display: flex; align-items: baseline; gap: 4px; margin-bottom: 16px; }
.balance-amount .currency { font-size: 18px; font-weight: 600; color: #047857; }
.balance-card.has-balance .balance-amount .currency { color: #92400e; }
.balance-amount .amount { font-size: 36px; font-weight: 800; color: #065f46; }
.balance-card.has-balance .balance-amount .amount { color: #78350f; }
.balance-details { display: flex; gap: 20px; padding-top: 16px; border-top: 1px solid rgba(0,0,0,0.1); }
.balance-item { }
.balance-item span { display: block; font-size: 11px; color: #047857; text-transform: uppercase; }
.balance-card.has-balance .balance-item span { color: #92400e; }
.balance-item strong { font-size: 14px; color: #065f46; }
.balance-card.has-balance .balance-item strong { color: #78350f; }

/* Payment Section */
.payment-section { margin-top: 24px; padding-top: 24px; border-top: 2px solid #e2e8f0; }
.stk-payment-form { background: #f8fafc; border-radius: 14px; padding: 24px; margin-bottom: 20px; }
.input-with-currency { position: relative; display: flex; }
.currency-prefix { position: absolute; left: 0; top: 0; bottom: 0; width: 56px; background: #e2e8f0; border: 1.5px solid #e2e8f0; border-right: none; border-radius: 10px 0 0 10px; display: flex; align-items: center; justify-content: center; font-weight: 600; color: #64748b; font-size: 14px; }
.form-input.with-prefix { padding-left: 68px; border-radius: 10px; }

.quick-amounts { display: flex; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
.quick-btn { padding: 10px 16px; background: white; border: 2px solid #e2e8f0; border-radius: 8px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.2s; }
.quick-btn:hover { border-color: #0f4c81; color: #0f4c81; background: #f0f9ff; }
.quick-btn.active { background: #0f4c81; border-color: #0f4c81; color: white; }

.manual-pay-info { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; padding: 20px; }
.manual-pay-info h4 { font-size: 14px; font-weight: 600; color: #1e40af; margin: 0 0 12px; }
.pay-details { display: flex; gap: 24px; }
.pay-item { }
.pay-item span { display: block; font-size: 11px; color: #3b82f6; text-transform: uppercase; }
.pay-item strong { font-size: 16px; color: #1e3a8a; }

/* Cleared Message */
.cleared-message { text-align: center; padding: 40px 20px; }
.cleared-icon { width: 80px; height: 80px; background: #d1fae5; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; color: #059669; }
.cleared-message h3 { font-size: 20px; font-weight: 700; color: #059669; margin: 0 0 8px; }
.cleared-message p { font-size: 14px; color: #64748b; margin: 0; }

/* Buttons */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 14px 24px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.3s; border: none; font-family: inherit; }
.btn-primary { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); color: white; box-shadow: 0 4px 15px rgba(15, 76, 129, 0.3); }
.btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(15, 76, 129, 0.4); }
.btn-secondary { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background: #e2e8f0; }
.btn-outline { background: transparent; color: #0f4c81; border: 2px solid #0f4c81; }
.btn-outline:hover { background: #0f4c81; color: white; }
.btn-success { background: linear-gradient(135deg, #059669 0%, #047857 100%); color: white; box-shadow: 0 4px 15px rgba(5, 150, 105, 0.3); }
.btn-success:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(5, 150, 105, 0.4); }
.btn-success:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
.btn-block { width: 100%; }

.action-buttons { display: flex; gap: 12px; margin-top: 24px; }

/* Spinner */
.spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; }
.spinner.small { width: 18px; height: 18px; }
.spinner.white { border-color: rgba(255,255,255,0.3); border-top-color: white; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Payment Modal */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 24px; backdrop-filter: blur(4px); }
.modal-container { background: white; border-radius: 20px; max-width: 440px; width: 100%; overflow: hidden; }
.payment-modal-header { padding: 40px 32px; text-align: center; color: white; }
.payment-modal-header.pending { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); }
.payment-modal-header.success { background: linear-gradient(135deg, #059669 0%, #047857 100%); }

.payment-loader { margin-bottom: 20px; }
.loader-ring { width: 60px; height: 60px; border: 4px solid rgba(255,255,255,0.2); border-top-color: white; border-radius: 50%; margin: 0 auto; animation: spin 1s linear infinite; }

.payment-success-icon { width: 80px; height: 80px; background: rgba(255,255,255,0.2); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
.payment-status-text { font-size: 14px; opacity: 0.9; margin-bottom: 8px; }
.payment-amount { font-size: 36px; font-weight: 800; margin: 0; }

.payment-modal-body { padding: 32px; }
.payment-instructions h4 { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 8px; }
.instruction-subtitle { font-size: 13px; color: #64748b; margin-bottom: 20px; }
.payment-instructions ol { list-style: none; padding: 0; margin: 0 0 24px; }
.payment-instructions li { display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #475569; }
.payment-instructions li:last-child { border-bottom: none; }
.step-num { width: 24px; height: 24px; background: #eff6ff; color: #2563eb; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }

.waiting-indicator { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 16px; background: #fffbeb; border-radius: 10px; font-size: 14px; color: #92400e; }
.pulse-dot { width: 10px; height: 10px; background: #f59e0b; border-radius: 50%; animation: pulse 1.5s ease-in-out infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.2); } }

.payment-success h4 { font-size: 18px; font-weight: 600; color: #1e293b; margin-bottom: 8px; }
.success-message { font-size: 14px; color: #64748b; margin-bottom: 20px; }
.success-details { background: #f8fafc; border-radius: 12px; padding: 20px; margin-bottom: 24px; text-align: left; }
.success-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
.success-row:last-child { border-bottom: none; }
.success-row span { color: #64748b; }
.success-row strong { color: #1e293b; }
.success-actions { display: flex; gap: 12px; }
.success-actions .btn { flex: 1; }

/* Modal Transitions */
.modal-enter-active, .modal-leave-active { transition: all 0.3s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .modal-container, .modal-leave-to .modal-container { transform: scale(0.95) translateY(20px); }

/* Responsive */
@media (max-width: 1024px) {
  .content-grid { grid-template-columns: 1fr; }
  .info-panel { position: static; }
}

@media (max-width: 768px) {
  .page-title { font-size: 28px; }
  .form-card-body { padding: 24px; }
  .details-grid { grid-template-columns: 1fr; }
  .input-with-btn { flex-direction: column; }
  .search-btn { width: 100%; height: 48px; }
  .action-buttons { flex-direction: column; }
  .success-actions { flex-direction: column; }
  .quick-amounts { flex-direction: column; }
  .pay-details { flex-direction: column; gap: 12px; }
  .balance-details { flex-direction: column; gap: 12px; }
  .balance-amount .amount { font-size: 28px; }
}
</style>